import React, { memo } from 'react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  startIndex: number;
  endIndex: number;
  totalItems: number;
  canGoNext: boolean;
  canGoPrev: boolean;
  onGoToPage: (page: number) => void;
  onGoToNextPage: () => void;
  onGoToPrevPage: () => void;
  onGoToFirstPage: () => void;
  onGoToLastPage: () => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
}

// Icons
const FirstPageIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M11 4L7 8l4 4M5 4v8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PrevPageIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M10 4L6 8l4 4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const NextPageIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M6 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LastPageIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M5 4l4 4-4 4M11 4v8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function PaginationComponent({
  currentPage,
  totalPages,
  pageSize,
  startIndex,
  endIndex,
  totalItems,
  canGoNext,
  canGoPrev,
  onGoToNextPage,
  onGoToPrevPage,
  onGoToFirstPage,
  onGoToLastPage,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100]
}: PaginationProps): React.ReactElement {
  const buttonStyle = (disabled: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border-default)',
    background: disabled ? 'var(--color-neutral-100)' : 'var(--color-bg-elevated)',
    color: disabled ? 'var(--color-text-tertiary)' : 'var(--color-text-primary)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all var(--transition-fast)',
    opacity: disabled ? 0.5 : 1
  });

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 'var(--space-4) var(--space-5)',
        borderTop: '1px solid var(--color-border-subtle)',
        background: 'var(--color-bg-secondary)',
        flexWrap: 'wrap',
        gap: 'var(--space-4)'
      }}
    >
      {/* Items per page selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <span
          style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--color-text-secondary)'
          }}
        >
          Sayfa başına:
        </span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          style={{
            padding: 'var(--space-2) var(--space-3)',
            fontSize: 'var(--text-sm)',
            fontFamily: 'var(--font-body)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border-default)',
            background: 'var(--color-bg-elevated)',
            color: 'var(--color-text-primary)',
            cursor: 'pointer',
            outline: 'none'
          }}
        >
          {pageSizeOptions.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      {/* Page info */}
      <div
        style={{
          fontSize: 'var(--text-sm)',
          color: 'var(--color-text-secondary)'
        }}
      >
        {totalItems > 0 ? (
          <>
            <span style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>
              {startIndex}-{endIndex}
            </span>{' '}
            /{' '}
            <span style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>
              {totalItems}
            </span>
          </>
        ) : (
          'Kayıt yok'
        )}
      </div>

      {/* Navigation buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
        <button
          onClick={onGoToFirstPage}
          disabled={!canGoPrev}
          style={buttonStyle(!canGoPrev)}
          title="İlk sayfa"
        >
          <FirstPageIcon />
        </button>
        <button
          onClick={onGoToPrevPage}
          disabled={!canGoPrev}
          style={buttonStyle(!canGoPrev)}
          title="Önceki sayfa"
        >
          <PrevPageIcon />
        </button>

        {/* Page indicator */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: '0 var(--space-3)',
            fontSize: 'var(--text-sm)'
          }}
        >
          <span style={{ color: 'var(--color-text-secondary)' }}>Sayfa</span>
          <span
            style={{
              padding: 'var(--space-1) var(--space-2)',
              minWidth: '32px',
              textAlign: 'center',
              fontWeight: 600,
              color: 'var(--color-primary-600)',
              background: 'var(--color-primary-50)',
              borderRadius: 'var(--radius-sm)'
            }}
          >
            {currentPage}
          </span>
          <span style={{ color: 'var(--color-text-secondary)' }}>/ {totalPages}</span>
        </div>

        <button
          onClick={onGoToNextPage}
          disabled={!canGoNext}
          style={buttonStyle(!canGoNext)}
          title="Sonraki sayfa"
        >
          <NextPageIcon />
        </button>
        <button
          onClick={onGoToLastPage}
          disabled={!canGoNext}
          style={buttonStyle(!canGoNext)}
          title="Son sayfa"
        >
          <LastPageIcon />
        </button>
      </div>
    </div>
  );
}

export const Pagination = memo(PaginationComponent);
